import React, { useContext, useEffect, useMemo, useRef } from "react";
import {
  Animated,
  Platform,
  ScrollView,
  StyleSheet,
  TouchableWithoutFeedback,
  useWindowDimensions,
  View,
} from "react-native";
import { useTheme } from "react-native-elements";
import { Gesture, GestureDetector } from "react-native-gesture-handler";
import { PlayerContext } from "../../backend/PlayerContext";
import { getFavoriteSpells } from "../../backend/spells/Spells";
import { useGestureHandler } from "../utility/Miscellaneous";
import { FavoriteSpellsContainer } from "./FavoriteSpellsContainer";
import SpellsContainer from "./SpellsContainer";

type Props = {
  visible: boolean;
  onBackgroundPress: () => void;
  onSwipeLeft: () => void;
  onSwipeRight: () => void;
  favoritesOnBottom: boolean;
};

export function SpellMenuContents(props: {
  onSwipeLeft: () => void;
  onSwipeRight: () => void;
  visible: boolean;
  favoritesOnBottom: boolean;
}) {
  const swipeHandlerGesture = useGestureHandler(
    props.onSwipeLeft,
    props.onSwipeRight,
  );
  const nativeGesture = useMemo(() => Gesture.Native(), []);
  const compositeGesture = useMemo(
    () => Gesture.Simultaneous(swipeHandlerGesture, nativeGesture),
    [swipeHandlerGesture, nativeGesture],
  );
  const swipeHandlerGestureBottom = useGestureHandler(
    props.onSwipeLeft,
    props.onSwipeRight,
  );
  const nativeGestureBottom = useMemo(() => Gesture.Native(), []);
  const compositeGestureBottom = useMemo(
    () => Gesture.Simultaneous(swipeHandlerGestureBottom, nativeGestureBottom),
    [swipeHandlerGestureBottom, nativeGestureBottom],
  );
  const theme = useTheme();
  const playerContext = useContext(PlayerContext);
  const isBottomMenuEnabled = playerContext.global.enableBottomMenu;
  const { height, width } = useWindowDimensions();

  const heightWithoutBottomMenu =
    height - (isBottomMenuEnabled && width < 640 ? 48 : 0);
  const hasFavorites = getFavoriteSpells(playerContext).length > 0;
  return useMemo(
    () => (
      <View style={{ flex: 1 }}>
        <GestureDetector
          key="spellMenuContentsGesture"
          gesture={compositeGesture}
        >
          <ScrollView
            style={{ flex: 1 }}
            contentContainerStyle={styles.magicContainer}
          >
            <SpellsContainer
              isVisible={props.visible}
              uiKey="SpellMenuContainer"
              onlyBySchool={props.favoritesOnBottom}
            />
          </ScrollView>
        </GestureDetector>
        {props.favoritesOnBottom && hasFavorites && (
          <View
            style={{
              height: Math.max(
                Math.min(heightWithoutBottomMenu - 472, 270),
                180,
              ),
              borderTopColor: theme.theme.colors.divider,
              borderTopWidth: 1,
            }}
          >
            <GestureDetector
              key="spellMenuContentsFavoritesGesture"
              gesture={compositeGestureBottom}
            >
              {Platform.select({
                default: (
                  <View style={[styles.favoritesContainer, { flex: 1 }]}>
                    <FavoriteSpellsContainer isVisible={props.visible} />
                  </View>
                ),
                web: (
                  <ScrollView contentContainerStyle={styles.favoritesContainer}>
                    <FavoriteSpellsContainer isVisible={props.visible} />
                  </ScrollView>
                ),
              })}
            </GestureDetector>
          </View>
        )}
      </View>
    ),
    [
      compositeGesture,
      compositeGestureBottom,
      props.visible,
      theme,
      hasFavorites,
      props.favoritesOnBottom,
      heightWithoutBottomMenu,
    ],
  );
}

export function SpellMenu(props: Props) {
  const positionAnim = useRef(
    new Animated.Value(props.visible ? 0 : 300),
  ).current;

  const opacityAnim = useRef(
    new Animated.Value(props.visible ? 0.5 : 0),
  ).current;

  const theme = useTheme();

  useEffect(() => {
    if (props.visible) {
      Animated.timing(positionAnim, {
        toValue: 0,
        duration: 150,
        useNativeDriver: true,
      }).start();
      Animated.timing(opacityAnim, {
        toValue: 0.5,
        duration: 150,
        useNativeDriver: true,
      }).start();
    } else {
      Animated.timing(positionAnim, {
        toValue: 300,
        duration: 150,
        useNativeDriver: true,
      }).start();
      Animated.timing(opacityAnim, {
        toValue: 0,
        duration: 150,
        useNativeDriver: true,
      }).start();
    }
  }, [props.visible]);

  return useMemo(() => {
    return (
      <>
        <TouchableWithoutFeedback
          accessibilityLabel="Close Spell Menu"
          accessibilityRole="button"
          accessibilityElementsHidden={true}
          importantForAccessibility="no-hide-descendants"
          onPress={props.onBackgroundPress}
        >
          <Animated.View
            style={[styles.backdrop, { opacity: opacityAnim }]}
            pointerEvents={props.visible ? "auto" : "none"}
          />
        </TouchableWithoutFeedback>
        <Animated.View
          style={[
            styles.container,
            {
              transform: [{ translateX: positionAnim }],
              backgroundColor: theme.theme.colors.background,
            },
          ]}
          accessibilityElementsHidden={!props.visible}
          importantForAccessibility={
            !props.visible ? "no-hide-descendants" : "auto"
          }
        >
          <SpellMenuContents
            visible={props.visible}
            onSwipeLeft={props.onSwipeLeft}
            onSwipeRight={props.onSwipeRight}
            favoritesOnBottom={props.favoritesOnBottom}
          />
        </Animated.View>
      </>
    );
  }, [
    opacityAnim,
    positionAnim,
    props.visible,
    props.onBackgroundPress,
    theme,
  ]);
}

const styles = StyleSheet.create({
  backdrop: {
    position: "absolute",
    height: "100%",
    width: "100%",
    backgroundColor: "#000",
    zIndex: 80,
  },
  backdropTouchable: {
    position: "absolute",
    height: "100%",
    width: "100%",
    zIndex: 90,
  },
  container: {
    justifyContent: "flex-start",
    alignItems: "stretch",
    position: "absolute",
    zIndex: 100,
    height: "100%",
    width: 300,
    right: 0,
    top: 0,
  },
  magicContainer: {
    padding: 8,
  },
  favoritesContainer: {
    paddingHorizontal: 8,
    paddingBottom: 8,
  },
});
